import React, { useState, useEffect } from "react";

import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { isAfter, parse, isValid } from "date-fns";

import {
  Flex,
  SimpleGrid,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  VStack,
  Divider,
  Text,
} from "@chakra-ui/react";

import {
  telefone as telMask,
  cpf,
  cep as cepMask,
  numeroEndereco,
  onlyNumbers,
  forNames,
} from "../../../../../utils/masks";

import DadosGeograficosService from "../../../../../../main/services/DadosGeograficosService";
import { CustomButton } from "../../../../../components/CustomButton";
import { CustomDatepickerWField } from "../../../../../components/CustomDatepickerWField";
import { CustomInputWField } from "../../../../../components/CustomInputWField";
import { CustomSelectWField } from "../../../../../components/CustomSelectWField";
import { CustomMultiSelectWField } from "../../../../../components/MultiSelect";
import { FocusError } from "../../../../../components/FocusError";
import CriancaService from "../../../../../../main/services/CriancaService";
import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { useAppContext } from "../../../../../../Context/MessageContextProvider";
import { hasFormChanged } from "../../../../../utils/hasFormChanged";
import { CustomTextArea } from "../../../../../components/CustomTextArea";
import { isValidCPF } from "../../../../../utils/isValidCPF";

export const IrmaosCadastrar = ({
  isOpen,
  onClose,
  idCrianca,
  fetchReferenciaData,
  fetchIrmaosData,
  setLoadingPainel,
  dataIrmaos,
}) => {
  const [bairros, setBairros] = useState({ options: [], isLoading: false });
  const [municipios, setMunicipios] = useState({
    options: [],
    isLoading: false,
  });

  const [dataFutura, setDataFutura] = useState(false);
  const [estados, setEstados] = useState({ options: [], isLoading: false });
  const [estado, setEstado] = useState();
  const [municipio, setMunicipio] = useState();
  const [opcoesFiliacao, setOpcoesFiliacao] = useState({
    options: [],
    isLoading: false,
  });
  const [isFocused, setIsFocused] = useState(false);
  const [isBlur, setIsBlur] = useState(false);
  const [invalidCPF, setInvalidCPF] = useState(false);
  const [criancaCPFValid, setCriancaCPFValid] = useState(false);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const { setCadastrarIrmao, cadastrarIrmao } = useAppContext();
  const [initialValues] = useState({
    cep: "",
    complemento: "",
    cpf: "",
    email: "",
    dataNascimento: "",
    idBairro: "",
    idEstado: "",
    idMunicipio: "",
    filiacao: "",
    informacoesComplementares: "",
    logradouro: "",
    nome: "",
    numeroIdentidade: "",
    numeroResidencia: "",
    pontoReferencia: "",
    telefone: "",
  });

  const backAlert = () => {
    if (cadastrarIrmao) {
      Swal.fire({
        title: "Tem certeza que deseja cancelar?",
        text: "As informações serão perdidas.",
        icon: "error",
        showCancelButton: true,
        confirmButtonColor: "#169499",
        cancelButtonColor: "#169499",
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
      }).then((result) => {
        if (result.isConfirmed) {
          onClose();
          setCadastrarIrmao(false);
        }
      });
    } else {
      onClose();
    }
  };
  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  useEffect(() => {
    setMunicipios((prevState) => ({ ...prevState, isLoading: true }));

    if (estado) {
      DadosGeograficosService.pesquisarMunicipios(estado.id).then((res) => {
        setMunicipios({ options: res.data, isLoading: false });
      });
    } else {
      setMunicipios({ options: [], isLoading: false });
    }
  }, [estado]);

  useEffect(() => {
    setBairros((prevState) => ({ ...prevState, isLoading: true }));

    if (municipio && estado) {
      DadosGeograficosService.pesquisarBairros(municipio.id).then((res) => {
        setBairros({ options: res.data, isLoading: false });
      });
    } else {
      setBairros({ options: [], isLoading: false });
    }
  }, [municipio, estado]);

  const handleGetEstados = () => {
    setEstados({ ...estados, isLoading: true });
    DadosGeograficosService.getEstados().then((res) => {
      setEstados({ options: res.data, isLoading: false });
    });
  };

  const handleGetFiliacao = (idCrianca) => {
    setOpcoesFiliacao({ ...opcoesFiliacao, isLoading: true });
    CriancaService.getFiliacao(idCrianca).then((res) => {
      setOpcoesFiliacao({
        options: res.data,
        isLoading: false,
      });
    });
  };

  const schema = Yup.object().shape(
    {
      filiacao: Yup.array().required("Este campo é obrigatório!"),
      cep: Yup.string(),
      complemento: Yup.string(),
      cpf: Yup.string()
        .nullable()
        .test("cpfValido", "", (value) => {
          if (!isValidCPF(value)) {
            return false;
          } else {
            return true;
          }
        })
        .test("unicidade", "", () => {
          if (criancaCPFValid) {
            return false;
          } else {
            return true;
          }
        }),
      email: Yup.string().trim().email("Email inválido!"),
      dataNascimento: Yup.string()
        .nullable()
        .test("validDate", "Data inválida!", (value) => {
          if (!!value) {
            if (value.length === 10) {
              return isValid(parse(value, "dd/MM/yyyy", new Date()));
            } else {
              return false;
            }
          } else {
            return true;
          }
        })
        .test("dataNascimentoAfterToday", "", (value) => {
          if (!value) {
            return true;
          } else {
            return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
          }
        }),

      id: Yup.string(),
      idBairro: Yup.mixed().when("idMunicipio", {
        is: (municipio) =>
          municipio ? municipio.id === 3233 || municipio.id === 3215 : false,
        then: Yup.mixed().required("Este campo é obrigatório!").nullable(),
      }),

      idEstado: Yup.mixed(),
      idMunicipio: Yup.mixed(),
      informacoesComplementares: Yup.string(),
      logradouro: Yup.string(),
      nome: Yup.string()
        .required("Este campo é obrigatório!")
        .trim("Este campo é obrigatório!"),

      numeroIdentidade: Yup.string(),
      numeroResidencia: Yup.number(),
      pontoReferencia: Yup.string(),
      status: Yup.string(),
      telefone: Yup.string()
        .min(14, "Telefone inválido!")
        .max(15, "Telefone inválido!"),
    },
    [
      ["cpf", "nome"],
      ["nome", "cpf"],
    ]
  );

  const FocusErrorData = () => {
    const { values } = useFormikContext();

    useEffect(() => {
      const validateDate = () => {
        const { dataNascimento, cpf } = values;
        const currentDate = new Date();

        if (dataNascimento) {
          const movementDate = parse(dataNascimento, "dd/MM/yyyy", new Date());
          if (isAfter(movementDate, currentDate)) {
            setDataFutura(true);
          } else {
            setDataFutura(false);
          }
        }
        if (cpf) {
          if (isValidCPF(cpf)) {
            setInvalidCPF(true);
            CriancaService.validaCPFCrianca(idCrianca, cpf)
              .then(() => setCriancaCPFValid(false))
              .catch((error) => {
                console.log(error);
                setCriancaCPFValid(true);
              });
          } else {
            setInvalidCPF(false);
          }
        } else {
          setCriancaCPFValid(false);
        }
      };

      validateDate();
    }, [values, values.dataNascimento]);

    return null;
  };

  const clearEndereco = (props) => {
    props.setFieldValue("logradouro", "");
    props.setFieldValue("numeroResidencia", "");
    props.setFieldValue("complemento", "");
    props.setFieldValue("cep", "");
    props.setFieldValue("pontoReferencia", "");
  };

  const handleSubmit = (values, actions) => {
    setTimeout(() => {
      const {
        nome,
        dataNascimento,
        cpf,
        filiacao,
        numeroIdentidade,
        email,
        informacoesComplementares,
        idEstado,
        idMunicipio,
        idBairro,
        logradouro,
        complemento,
        pontoReferencia,
        numeroResidencia,
        telefone,
        cep,
      } = values;

      const valuesToSend = {
        filiacao: filiacao ? filiacao[0] : null,
        nome: nome ? nome.trim() : "",
        dataNascimento: dataNascimento
          ? parse(dataNascimento, "dd/MM/yyyy", new Date()).toISOString()
          : "",
        cpf: cpf ? cpf.trim() : "",
        numeroIdentidade: numeroIdentidade ? numeroIdentidade.trim() : "",
        email: email ? email.trim() : "",
        informacoesComplementares: informacoesComplementares
          ? informacoesComplementares.trim()
          : "",

        id: 0,
        idEstado: idEstado ? idEstado.id : "",
        idMunicipio: idMunicipio ? idMunicipio.id : "",
        idBairro: idBairro ? idBairro.id : "",
        logradouro: logradouro ? logradouro.trim() : "",
        complemento: complemento ? complemento.trim() : "",
        pontoReferencia: pontoReferencia ? pontoReferencia.trim() : "",
        cep: cep,
        numeroResidencia: numeroResidencia,
        telefone: telefone,
      };

      CriancaService.postIrmao(idCrianca, valuesToSend)
        .then((res) => {
          onClose();
          fetchIrmaosData();
          setLoadingPainel(true);
          Swal.fire({
            title: "Sucesso!",
            text: "Registro cadastrado com sucesso!",
            icon: "success",
            confirmButtonText: "Continuar",
            confirmButtonColor: "#169499",
          });
        })
        .catch((error) => {
          console.log(error.response.data);
          Swal.fire({
            title: "Atenção!",
            text: error.response.data.message
              ? error.response.data.message
              : "Erro de Conflito",
            icon: "error",
            confirmButtonText: "Fechar",
            confirmButtonColor: "#169499",
          });
        });

      actions.setSubmitting(false);
    }, 200);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        scrollBehavior={"inside"}
      >
        <ModalOverlay />
        <ModalContent
          p={{ base: "5px", sm: "15px", md: "25px" }}
          w={{ base: "100%", lg: "70%" }}
          bgColor="#FFF"
        >
          <ModalHeader
            p="0px"
            fontWeight={700}
            fontSize="14px"
            lineHeight="19.6px"
            color="#169499"
          >
            {"Cadastrar Irmão".toUpperCase()}
          </ModalHeader>
          <ModalBody
            p="0px"
            mt="25px"
            minH={{ base: "0px", md: "200px" }}
            pl="1px"
          >
            <Formik
              validationSchema={schema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {(props) => (
                <Form style={{ width: "100%" }}>
                  {setCadastrarIrmao(
                    hasFormChanged(initialValues, props.values)
                  )}
                  <FocusError />
                  <FocusErrorData />
                  <Grid templateColumns="repeat(4, 1fr)" gap="15px" w="100%">
                    <GridItem colSpan={{ base: 4, lg: 3 }}>
                      <CustomInputWField
                        fieldName="nome"
                        label="Nome*"
                        placeholder="Informe o Nome"
                        onChange={(e) =>
                          props.setFieldValue("nome", forNames(e.target.value))
                        }
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 4, lg: 1 }}>
                      <VStack alignItems="flex-start">
                        <CustomDatepickerWField
                          fieldName="dataNascimento"
                          label="Data de Nascimento"
                          placeholder="DD/MM/AAAA"
                          maxDate={new Date()}
                        />
                      </VStack>
                      {dataFutura && props.values.dataNascimento && (
                        <Text
                          mt="8px"
                          color="#E53E3E"
                          fontSize="sm"
                          lineHeight={"normal"}
                        >
                          A data informada não pode ser uma data futura!
                        </Text>
                      )}
                    </GridItem>

                    <GridItem colSpan={{ base: 4, lg: 2 }}>
                      {/* <CustomSelectWField
                          fieldName="idTipoReferencia"
                          label="Filiação*"
                          placeholder="Selecione a Filiação"
                          getOptionLabel={(option) => option.label}
                          getOptionValue={(option) => option.value}
                          isLoading={opcoesFiliacao.isLoading}
                          options={opcoesFiliacao.options}
                          onChange={(value) => {
                            props.setFieldValue("idTipoReferencia", value);
                          }}
                          onFocus={
                            opcoesFiliacao.options.length === 0
                              ? handleGetTipoReferencia
                              : null
                          }
                          isDisabled={!!initialFiliacao}
                        /> */}

                      <CustomMultiSelectWField
                        fieldName="filiacao"
                        label="Filiação em Comum*"
                        placeholder="Selecione a Filiação"
                        getOptionLabel={(option) => option.nome}
                        getOptionValue={(option) => option.id}
                        isLoading={opcoesFiliacao.isLoading}
                        options={opcoesFiliacao.options}
                        values={props?.values?.filiacao[0] || ""}
                        onChange={(value) => {
                          props.setFieldValue("filiacao", value ? value : "");
                        }}
                        onFocus={
                          opcoesFiliacao.options.length === 0
                            ? () => handleGetFiliacao(idCrianca)
                            : null
                        }
                        visible={true}
                      />
                    </GridItem>
                    <GridItem colSpan={{ base: 4, lg: 2 }} />

                    <GridItem colSpan={{ base: 4, lg: 1 }}>
                      <CustomInputWField
                        fieldName="cpf"
                        label="CPF"
                        placeholder="000.000.000-00"
                        maxLength={14}
                        onChange={(e) =>
                          props.setFieldValue("cpf", cpf(e.target.value))
                        }
                        setIsFocused={setIsFocused}
                        setIsBlur={
                          props?.values?.cpf?.replace(/[^\d]+/g, "").length ===
                            11 && setIsBlur
                        }
                      />

                      {props.values.cpf &&
                        !isFocused &&
                        !invalidCPF &&
                        !isBlur && (
                          <Text
                            color="red"
                            opacity={0.8}
                            mt={2}
                            fontSize="12px"
                          >
                            Informar um CPF válido
                          </Text>
                        )}
                      {props.values.cpf &&
                        !isFocused &&
                        criancaCPFValid &&
                        invalidCPF &&
                        !isBlur && (
                          <Text
                            color="red"
                            opacity={0.8}
                            mt={2}
                            fontSize="12px"
                          >
                            CPF já cadastrado nessa ficha.
                          </Text>
                        )}
                    </GridItem>

                    <GridItem colSpan={{ base: 4, lg: 1 }}>
                      <CustomInputWField
                        fieldName="numeroIdentidade"
                        label="Identidade"
                        placeholder="Informe a Identidade"
                        maxLength={15}
                        onChange={(e) =>
                          props.setFieldValue(
                            "numeroIdentidade",
                            onlyNumbers(e.target.value)
                          )
                        }
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 4, lg: 1 }}>
                      <CustomInputWField
                        fieldName="telefone"
                        label="Telefone"
                        placeholder="(99) 99999-9999"
                        maxLength={15}
                        onChange={(e) =>
                          props.setFieldValue(
                            "telefone",
                            telMask(e.target.value)
                          )
                        }
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 4, lg: 1 }}>
                      <CustomInputWField
                        fieldName="email"
                        label="E-mail"
                        placeholder="Informe o e-mail"
                        onChange={(e) =>
                          props.setFieldValue(
                            "email",
                            e.target.value.toLowerCase()
                          )
                        }
                        maxLength={80}
                      />
                    </GridItem>

                    <GridItem colSpan={4}>
                      <CustomTextArea
                        fieldName={"informacoesComplementares"}
                        label={"Informações Complementares"}
                        placeholder={"Informe as Informações Complementares"}
                        setIsTextareaFocused={setIsTextareaFocused}
                        maxLength={255}
                        isTextareaFocused={isTextareaFocused}
                        fontWeightLabel={500}
                        colorLabel="#5F6377"
                        fontSizeLabel={"14px"}
                        lineHeightLabel={"19.6px"}
                      />
                    </GridItem>
                  </Grid>

                  <Divider mt="15px" />
                  <Text
                    color="#5F6377"
                    fontWeight={500}
                    fontSize="14px"
                    lineHeight="19.6px"
                    mt="15px"
                  >
                    Endereço
                  </Text>

                  <Grid
                    templateColumns="repeat(9, 1fr)"
                    gap="15px"
                    mt="15px"
                    w="100%"
                  >
                    <GridItem colSpan={{ base: 9, lg: 3 }}>
                      <CustomSelectWField
                        fieldName="idEstado"
                        label="Estado"
                        placeholder="Selecione o Estado"
                        getOptionLabel={(option) => option.nome}
                        getOptionValue={(option) => option.id}
                        isLoading={estados.isLoading}
                        options={estados.options}
                        onChange={(value) => {
                          if (value && props.values.idEstado) {
                            if (value.nome !== props.values.idEstado.nome) {
                              props.setFieldValue("idMunicipio", "");
                              props.setFieldValue("idBairro", "");
                            }
                          } else {
                            props.setFieldValue("idMunicipio", "");
                            props.setFieldValue("idBairro", "");
                          }
                          setEstado(value);
                          setMunicipio(false);
                          props.setFieldValue("idEstado", value);
                          clearEndereco(props);
                        }}
                        onFocus={
                          estados.options.length === 0 ? handleGetEstados : null
                        }
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 9, lg: 3 }}>
                      <CustomSelectWField
                        fieldName="idMunicipio"
                        label="Cidade"
                        placeholder="Selecione a Cidade"
                        getOptionLabel={(option) => option.nome}
                        getOptionValue={(option) => option.id}
                        isLoading={municipios.isLoading}
                        options={municipios.options}
                        onChange={(value) => {
                          setMunicipio(value);
                          props.setFieldValue("idMunicipio", value);
                          props.setFieldValue("idBairro", "");
                          clearEndereco(props);
                        }}
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 9, lg: 3 }}>
                      <CustomSelectWField
                        fieldName="idBairro"
                        label={`Bairro${
                          props?.values?.idMunicipio?.id === 3233 ||
                          props?.values?.idMunicipio?.id === 3215
                            ? "*"
                            : ""
                        }`}
                        placeholder="Selecione o Bairro"
                        getOptionLabel={(option) => option.nome}
                        getOptionValue={(option) => option.id}
                        isLoading={bairros.isLoading}
                        options={bairros.options}
                        onChange={(value) => {
                          props.setFieldValue("idBairro", value);
                          !value && clearEndereco(props);
                        }}
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 9, lg: 7 }}>
                      <CustomInputWField
                        fieldName="logradouro"
                        label="Logradouro"
                        placeholder="Informe o Logradouro"
                        maxLength={80}
                        isDisabled={
                          !props.values.idEstado ||
                          !props.values.idMunicipio ||
                          !props.values.idBairro
                        }
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 9, lg: 2 }}>
                      <CustomInputWField
                        fieldName="numeroResidencia"
                        label="Número"
                        placeholder="Informe o Número"
                        maxLength={14}
                        onChange={(e) =>
                          props.setFieldValue(
                            "numeroResidencia",
                            numeroEndereco(e.target.value)
                          )
                        }
                        isDisabled={
                          !props.values.idEstado ||
                          !props.values.idMunicipio ||
                          !props.values.idBairro
                        }
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 9, lg: 7 }}>
                      <CustomInputWField
                        fieldName="complemento"
                        label="Complemento"
                        placeholder="Informe o Complemento"
                        maxLength={255}
                        isDisabled={
                          !props.values.idEstado ||
                          !props.values.idMunicipio ||
                          !props.values.idBairro
                        }
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 9, lg: 2 }}>
                      <CustomInputWField
                        fieldName="cep"
                        label="CEP"
                        placeholder="Informe o CEP"
                        maxLength={9}
                        onChange={(e) =>
                          props.setFieldValue("cep", cepMask(e.target.value))
                        }
                        isDisabled={
                          !props.values.idEstado ||
                          !props.values.idMunicipio ||
                          !props.values.idBairro
                        }
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 9, lg: 9 }}>
                      <CustomInputWField
                        fieldName="pontoReferencia"
                        label="Ponto de Referência"
                        placeholder="Informe o Ponto de Referência"
                        maxLength={2000}
                        isDisabled={
                          !props.values.idEstado ||
                          !props.values.idMunicipio ||
                          !props.values.idBairro
                        }
                      />
                    </GridItem>
                  </Grid>
                  <Flex justifyContent="end">
                    <SimpleGrid
                      mt="25px"
                      columns={2}
                      spacing="15px"
                      w={{ base: "100%", lg: "40%" }}
                    >
                      <CustomButton onClick={backAlert} label="Cancelar" />
                      <CustomButton
                        type="submit"
                        translate="no"
                        isLoading={props.isSubmitting}
                        label="Salvar"
                      />
                    </SimpleGrid>
                  </Flex>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
